<template>
  <div :class="$style.method">
    <img
      v-if="icons[type].icon"
      :src="icons[type].icon"
      :alt="title"
      :class="$style.icon"
      :style="{ background: icons[type].fallback }"
    >

    <div
      :class="$style.title"
      data-qa="integration-title"
    >
      {{ title }}
    </div>

    <slot
      name="connection"
      data-qa="integration-connect-button"
    >
      <ui-button
        v-if="!isConnected"
        data-qa="integration-connect-button"
        :loading="loading"
        size="s"
        type="secondary"
        @click="connect"
      >
        {{ i18n('connect') }}
      </ui-button>
    </slot>

    <template v-if="isConnected">
      <div
        data-qa="account"
        :class="$style.account"
      >
        {{ displayName }}
      </div>
      <span
        :class="$style.disconnect"
        data-qa="integration-disconnect-button"
        @click="$emit('disconnect', type)"
      >{{ i18n('disconnect') }}</span>
    </template>

    <div
      v-if="error"
      :class="$style.error"
    >
      {{ error }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { UiButton } from '@huntflow/ui';
import { computed } from 'vue';
import { useI18N } from '@/shared/legacy/lib/use-i18n';
import { AuthType } from '../../api/integration';
import { appConfig } from '../../lib/config/app-config';

const { type, isConference, error = null, displayName = null } = defineProps<{
  type: string;
  isConference?: boolean;
  loading?: boolean;
  error?: string;
  displayName?: string;
  isConnected?: boolean;
}>();

const emit = defineEmits<{
  disconnect: [];
  connect: [];
}>();

const icons: {
  [key: string]: {
    icon: string;
    fallback: string;
  };
} = {
  RABOTARU: {
    icon: require('./rabotaru.svg'),
    fallback: '#3495DB'
  },
  LI: {
    icon: require('./li.svg'),
    fallback: '#2D75B6'
  },
  AH: {
    icon: appConfig.get('instanceRegion') === 'RU' ? require('./matchy.svg') : require('./ah.svg'),
    fallback: '#EAEAEA'
  },
  HH: {
    icon: require('./hh.svg'),
    fallback: '#D6001D'
  },
  VK: {
    icon: require('./vk.svg'),
    fallback: '#55759F'
  },
  MK: {
    icon: require('./mk.svg'),
    fallback: '#759AA8'
  },
  SJ: {
    icon: require('./sj.svg'),
    fallback: '#4BA789'
  },
  ZPRU: {
    icon: require('./zpru.svg'),
    fallback: '#EABB52'
  },
  ZPRUHH: {
    icon: require('./zpru.svg'),
    fallback: '#EABB52'
  },
  PODBOR: {
    icon: require('./podbor.svg'),
    fallback: '#4687F0'
  },
  AVITO: {
    icon: require('./avito.svg'),
    fallback: '#FFF'
  },
  GMATE: {
    icon: require('./gmate.svg'),
    fallback: '#FFE54B'
  },
  ZOOM: {
    icon: require('./zoom.svg'),
    fallback: '#4A8CFF'
  },
  OUTLOOK365: {
    icon: require('./outlook365.svg'),
    fallback: '#FFF'
  },
  GOOGLE: {
    icon: require('./googlemeet.svg'),
    fallback: '#FFF'
  },
  RABOTABY: {
    icon: require('./rabotaby.svg'),
    fallback: '#E80200'
  },
  YANDEX_OAUTH2: {
    icon: require('./yandex.telemost.svg'),
    fallback: '#0E1217'
  },
  MTSLINK: {
    icon: require('./mtslink.svg'),
    fallback: '#FF0131'
  },
  KONTUR_TALK: {
    icon: require('./kontur-talk.svg'),
    fallback: '#51ADFF'
  }
};

const i18n = useI18N();

const title = computed(() => {
  let finalType = type;
  if (type === AuthType.ah && appConfig.get('instanceRegion') === 'RU') {
    finalType = 'MATCHY';
  }
  return isConference
    ? i18n(`auth_type.conference.${finalType}`)
    : i18n(`auth_type.${finalType}`);
});

const connect = () => {
  emit('connect', type);
};
</script>

<style module>
.method {
  font-size: $baseFontSize;
  line-height: $secondaryLineHeight;

  position: relative;

  padding-left: 75px;
  min-height: 60px;
  vertical-align: top;
}

.title,
.account {
  @mixin ellipsis;
  margin-bottom: 5px;
}

/* Правило для firefox – в результате рендеринга обрезается нижний выносной элемент */
.title {
  line-height: 20px;
  margin-bottom: 3px;
}

.account {
  font-weight: 500;
}

.disconnect {
  display: inline-block;
  color: #999999;
  user-select: none;
  cursor: pointer;

  &:hover {
    color: $red-400;
  }
}

.icon {
  position: absolute;
  top: 0;
  left: 0;
  border-radius: 50%;
  font-size: 0;

  width: 60px;
  height: 60px;
}

.error {
  composes: form-group__error from global;
  composes: form-group__error_visible from global;
  display: block;
}
</style>

<i18n lang="json" base="/src/modules/auth-type.lang.json">
{
  "connect": {
    "ru_RU": "Подключить",
    "en_US": "Connect"
  },
  "disconnect": {
    "ru_RU": "Отключить",
    "en_US": "Disconnect"
  }
}
</i18n>
